import React, { useEffect } from 'react';
// --- Navigation
import { useNavigate, useLocation } from 'react-router-dom';
// --- Styles
import './styles.scss';
// --- UI Components
import { ExpandableMenu, ExpandableMenuSection } from '@dtci/spellbook/ExpandableMenu';
import Icon from '@dtci/spellbook/Icon';
import { DualPanelLayout, PrimaryPanel, SecondaryPanel } from '@dtci/spellbook/DualPanelLayout';
import { AppFooter } from 'datastage/components/AppFooter';
import { usePermissions } from '../../utils/permissions';

export const PageNavigation = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const {
        isAdmin,
        isRLProductGateUser,
        isRLProductGateEspnPlusUser,
        isJobTrackerUser,
        isBulkDateMathToolUser,
        isRLImportTemplateUser,
    } = usePermissions();

    const routePermissions = {
        '/uploadTemplates': isRLImportTemplateUser,
        '/jobs': isJobTrackerUser,
        '/dateMath': isBulkDateMathToolUser,
        '/products': true,
        '/rightslineProductGate': isAdmin || isRLProductGateUser || isRLProductGateEspnPlusUser,
        '/dealVersion': true,
        '/users': isAdmin,
        '/rights': true,
        '/pmxDeliveries': true,
        '/dealDROChanges': true,
    };

    useEffect(() => {
        const allowed = routePermissions[location.pathname];
        if (allowed === false) {
            navigate('/login', { replace: true });
        }
        if (localStorage.getItem('activePanel') !== location.pathname) {
            localStorage.setItem('activePanel', location.pathname);
        }
        if (localStorage.getItem('activePanelSearch') !== location.search) {
            localStorage.setItem('activePanelSearch', location.search);
        }
    }, [location.pathname, location.search]);

    const getSearchKey = (path) => `searchParams:${path}`;

    const goTo = (path) => () => {
        localStorage.setItem(getSearchKey(location.pathname), location.search);

        const savedSearch = localStorage.getItem(getSearchKey(path));
        navigate(`${path}${savedSearch || ''}`);
        localStorage.setItem('activePanel', path);
    };


    return (
        <>
            <div>
                <DualPanelLayout>
                    <PrimaryPanel>{children}</PrimaryPanel>
                    <SecondaryPanel fill="theme" strokeWidth="thin">
                        <ExpandableMenu isExpanded>
                            <br />

                            {isRLImportTemplateUser && (
                                <>
                                    <ExpandableMenuSection
                                        isActive={
                                            localStorage.getItem('activePanel') ===
                                            '/uploadTemplates'
                                        }
                                        mainSection={
                                            <Icon
                                                altText="upload"
                                                iconName="fa-solid fa-arrow-up-from-bracket"
                                                title="upload"
                                                iconType="fa"
                                            />
                                        }
                                        onClick={goTo('/uploadTemplates')}
                                    >
                                        Upload Templates
                                    </ExpandableMenuSection>
                                    <br />
                                </>
                            )}

                            {isJobTrackerUser && (
                                <>
                                    <ExpandableMenuSection
                                        isActive={localStorage.getItem('activePanel') === '/jobs'}
                                        mainSection={
                                            <Icon
                                                altText="jobs"
                                                faPackage="far"
                                                iconName="fa-solid fa-briefcase"
                                                iconType="fa"
                                                title="jobs"
                                            />
                                        }
                                        onClick={goTo('/jobs')}
                                    >
                                        Jobs
                                    </ExpandableMenuSection>
                                    <br />
                                </>
                            )}

                            {isBulkDateMathToolUser && (
                                <>
                                    <ExpandableMenuSection
                                        isActive={
                                            localStorage.getItem('activePanel') === '/dateMath'
                                        }
                                        mainSection={
                                            <Icon
                                                altText="dateMath"
                                                faPackage="fas"
                                                iconName="fa-solid fa-calculator"
                                                iconType="fa"
                                                title="dateMath"
                                            />
                                        }
                                        onClick={goTo('/dateMath')}
                                    >
                                        Date Math
                                    </ExpandableMenuSection>
                                    <br />
                                </>
                            )}

                            <ExpandableMenuSection
                                isActive={localStorage.getItem('activePanel') === '/products'}
                                mainSection={
                                    <Icon
                                        altText="products"
                                        faPackage="fas"
                                        iconName="fa-clapperboard"
                                        iconType="fa"
                                        title="products"
                                    />
                                }
                                onClick={goTo('/products')}
                            >
                                Products
                            </ExpandableMenuSection>
                            <br />

                            {(isAdmin || isRLProductGateUser || isRLProductGateEspnPlusUser) && (
                                <>
                                    <ExpandableMenuSection
                                        isActive={
                                            localStorage.getItem('activePanel') ===
                                            '/rightslineProductGate'
                                        }
                                        mainSection={
                                            <Icon
                                                altText="rightslineProductGate"
                                                faPackage="fas"
                                                iconName="fa-solid fa-arrows-up-down"
                                                iconType="fa"
                                                title="rightslineProductGate"
                                            />
                                        }
                                        onClick={goTo('/rightslineProductGate')}
                                    >
                                        Rightsline Product Gate
                                    </ExpandableMenuSection>
                                    <br />
                                </>
                            )}

                            <ExpandableMenuSection
                                isActive={localStorage.getItem('activePanel') === '/dealVersion'}
                                mainSection={
                                    <Icon
                                        altText="dealVersion"
                                        faPackage="fas"
                                        iconName="fa-solid fa-code-compare"
                                        iconType="fa"
                                        title="dealVersion"
                                    />
                                }
                                onClick={goTo('/dealVersion')}
                            >
                                Deal Version
                            </ExpandableMenuSection>
                            <br />

                            {isAdmin && (
                                <>
                                    <ExpandableMenuSection
                                        isActive={localStorage.getItem('activePanel') === '/users'}
                                        mainSection={
                                            <Icon
                                                altText="users"
                                                faPackage="fas"
                                                iconName="fa-solid fa-users"
                                                iconType="fa"
                                                title="users"
                                            />
                                        }
                                        onClick={goTo('/users')}
                                    >
                                        Users
                                    </ExpandableMenuSection>
                                    <br />
                                </>
                            )}

                            <ExpandableMenuSection
                                isActive={localStorage.getItem('activePanel') === '/rights'}
                                mainSection={
                                    <Icon
                                        altText="rights"
                                        faPackage="fas"
                                        iconName="fa-solid fa-bookmark"
                                        iconType="fa"
                                        title="rights"
                                    />
                                }
                                onClick={goTo('/rights')}
                            >
                                Rights
                            </ExpandableMenuSection>
                            <br />
                            <ExpandableMenuSection
                                isActive={localStorage.getItem('activePanel') === '/pmxDeliveries'}
                                mainSection={
                                    <Icon
                                        altText="pmxDeliveries"
                                        faPackage="fas"
                                        iconName="fa-solid fa-dollar-sign"
                                        iconType="fa"
                                        title="pmxDeliveries"
                                    />
                                }
                                onClick={goTo('/pmxDeliveries')}
                            >
                                PMX Deliveries
                            </ExpandableMenuSection>
                            <br />
                        </ExpandableMenu>
                    </SecondaryPanel>
                </DualPanelLayout>
                <AppFooter />
            </div>
        </>
    );
};
